import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ApiService } from '../services/api.service';
import { extractList } from '../shared/utils/extract-list';

interface PayrollRecord {
  id: number | string;
  employeeName: string;
  department: string;
  basicSalary: number;
  allowances: number;
  deductions: number;
  netSalary: number;
  status: string;
}

@Component({
  selector: 'app-payroll',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './payroll.component.html',
  styleUrl: './payroll.component.scss'
})
export class PayrollComponent implements OnInit {

  loading = false;
  searchText = '';
  selectedMonth = new Date().toISOString().slice(0, 7);
  error: string | null = null;

  records: PayrollRecord[] = [];
  filteredRecords: PayrollRecord[] = [];
  statuses = ['All', 'Processed', 'Pending'];

  displayedColumns: string[] = ['employee', 'department', 'basic', 'allowances', 'deductions', 'net', 'status'];

  totalPayroll = 0;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadPayroll();
  }

  loadPayroll(): void {
    this.loading = true;
    this.error = null;

    this.apiService.getPayroll().subscribe({
      next: (response) => {
        const data = extractList(response);
        this.records = data.map((row: any) => ({
          id: row.id || Math.random(),
          employeeName:
            row.employeeName ||
            row.employee?.displayName ||
            row.employee?.name ||
            'Employee',
          department: row.department || row.departmentName || 'N/A',
          basicSalary: Number(row.basicSalary ?? row.basic ?? 0),
          allowances: Number(row.allowances ?? 0),
          deductions: Number(row.deductions ?? 0),
          netSalary: Number(row.netSalary ?? row.net ?? 0),
          status: String(row.status ?? 'Pending')
        }));
        this.filteredRecords = [...this.records];
        this.calculateTotal();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.message || err?.message || 'Failed to load payroll.';
        this.records = [];
        this.filteredRecords = [];
        this.calculateTotal();
      }
    });
  }

  calculateTotal(): void {
    this.totalPayroll = this.records.reduce((sum, r) => sum + r.netSalary, 0);
  }

  filterRecords(): void {
    const search = this.searchText.toLowerCase().trim();
    this.filteredRecords = this.records.filter(r =>
      r.employeeName.toLowerCase().includes(search) ||
      r.department.toLowerCase().includes(search)
    );
  }

  refresh(): void {
    this.loadPayroll();
  }

  getPendingCount(): number {
    return this.records.filter(r => r.status === 'Pending').length;
  }

  getProcessedCount(): number {
    return this.records.filter(r =>
      String(r.status).toLowerCase() === 'processed' ||
      String(r.status).toLowerCase() === 'paid'
    ).length;
  }
}
